import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FastfoodOutlinedIcon from '@mui/icons-material/FastfoodOutlined';

import OrderDetailsDialog from './OrderDetailsDialog';
import OrderPaymentDialog from './OrderPaymentDialog';

import api from 'utils/api';
import { pluralize } from 'utils/pluralize';
import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import { useToast } from 'contexts/ToastContext';
import { useCurrency } from 'contexts/CurrencyContext';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import DirhamSymbol from 'components/currency/DirhamSymbol';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableActionCell from 'components/table/TableActionCell';
import ClickableTableRow from 'components/table/ClickableTableRow';
import {
  orderStatusChipSx,
  paymentStatusChipSx,
  normalizeOrderStatusKey
} from 'components/table/tableChipStyles';
import { brand, destructive } from 'utils/designTokens';

/** API order_status values (lowercase) */
export const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'pickedup', label: 'PickedUp' },
  { value: 'outfordelivery', label: 'OutForDelivery' },
  { value: 'served', label: 'Served' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' }
];

function parseOrderListResponse(res) {
  const body = res?.data;
  const pagination = body?.pagination;
  const raw = body?.data ?? body;

  const orders = Array.isArray(raw)
    ? raw
    : raw?.orders ?? raw?.order_list ?? raw?.orderList ?? raw?.list ?? [];

  const list = Array.isArray(orders) ? orders : [];

  const total =
    pagination?.total ??
    pagination?.total_count ??
    pagination?.totalCount ??
    (Array.isArray(raw) ? undefined : raw?.total) ??
    raw?.count ??
    body?.total ??
    list.length;

  return {
    orders: list,
    total: Number(total) || 0,
    currentPage: Number(pagination?.page) || 1,
    totalPages: Number(pagination?.total_pages) || 1
  };
}

const PAGE_LIMIT = 12;

const tableScrollSx = {
  display: 'block',
  maxHeight: { xs: '55vh', md: 540 },
  minHeight: 360,
  overflowX: 'auto',
  overflowY: 'scroll',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(31, 91, 64, 0.45) rgba(31, 91, 64, 0.08)',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(31, 91, 64, 0.4)',
    borderRadius: 8,
    border: '2px solid rgba(31, 91, 64, 0.06)'
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(31, 91, 64, 0.08)',
    borderRadius: 8
  }
};

function getOrderNo(order) {
  return order?.order_no ?? order?.order_number ?? order?.order_id ?? '—';
}

function getRawOrderStatus(order) {
  return (
    order?.order_status ??
    order?.latest_status?.order_status ??
    order?.status ??
    ''
  );
}

export function resolveOrderStatus(order) {
  const raw = String(getRawOrderStatus(order)).trim();
  if (!raw) return '';

  const key = normalizeOrderStatusKey(raw);
  const match = ORDER_STATUSES.find((s) => s.value === key);
  return match?.value ?? key;
}

function getOrderStatusLabel(order) {
  const key = resolveOrderStatus(order);
  if (!key) return '—';
  const found = ORDER_STATUSES.find((s) => s.value === key);
  return found?.label ?? key;
}

function getPaymentStatus(order) {
  return order?.payment_status ?? '—';
}

function getItemCount(order) {
  if (order?.item_count != null) return Number(order.item_count);
  if (Array.isArray(order?.items)) return order.items.length;
  return 0;
}

function formatDate(val) {
  if (!val) return '—';
  const d = dayjs(val);
  if (!d.isValid()) return '—';

  const timeStr = d.toDate().toLocaleString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const today = dayjs().startOf('day');
  const orderDay = d.startOf('day');

  if (orderDay.isSame(today, 'day')) {
    return `Today, ${timeStr}`;
  }
  if (orderDay.isSame(today.subtract(1, 'day'), 'day')) {
    return `Yesterday, ${timeStr}`;
  }

  return d.toDate().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getOrderCustomer(order) {
  const customer = order?.customer;
  if (!customer || typeof customer !== 'object') return null;
  return customer;
}

function CustomerDetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right', flex: 1, minWidth: 0 }}>{value}</Box>
    </Stack>
  );
}

CustomerDetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

const filterFieldSx = {
  width: { xs: '100%', md: 200 },
  maxWidth: { xs: '100%', md: 200 },
  flexShrink: 0,
  '& .MuiOutlinedInput-root': { borderRadius: 2 }
};

const datePickerSlotProps = {
  textField: {
    size: 'small',
    sx: filterFieldSx
  }
};

/**
 * Enhanced Order Items Dialog showing unit price, discount price, item discount amount, notes,
 * and authoritative reconciled financials.
 */
function OrderItemsDialog({ open, order, onClose, onViewDetails }) {
  const { currency } = useCurrency();
  const items = Array.isArray(order?.items) ? order.items : [];

  if (!order) return null;

  const subTotal = Number(order.sub_total || 0);
  const itemDiscounts = Number(order.total_discount || 0);
  const couponDiscount = Number(order.coupon_discount || 0);
  const deliveryCharge = Number(order.delivery_charge || 0);
  const taxTotal = Number(order.tax_total || 0);
  const grandTotal = Number(order.grand_total != null ? order.grand_total : order.total || 0);
  const paidAmount = Number(order.paid || 0);
  const balanceDue = Number(order.balance_due != null ? order.balance_due : Math.max(0, grandTotal - paidAmount));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Order Items Breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getOrderNo(order)} · Token #{order.token_no ?? '—'}
            </Typography>
          </Box>
          {order.order_status && (
            <Chip
              label={order.order_status}
              size="small"
              sx={orderStatusChipSx(order.order_status)}
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={3} align="center">
            No items recorded in this order.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: brand.canvas }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Product &amp; Variant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Unit Discount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Item Discount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((line, idx) => {
                  const unitPrice = Number(line.price || 0);
                  const unitDisc = Number(line.discount_price || 0);
                  const itemDisc = Number(line.discount_amount || unitDisc * Number(line.qty || 1));
                  const lineTotal = Number(line.total != null ? line.total : (unitPrice - unitDisc) * Number(line.qty || 1));

                  return (
                    <TableRow key={line.id || idx}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {line.product?.name || line.name || 'Unnamed Product'}
                        </Typography>
                        {line.variant?.name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Variant: {line.variant.name}
                          </Typography>
                        )}
                        {line.note && (
                          <Typography variant="caption" color="primary.main" display="block">
                            Note: {line.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600}>
                          {line.qty}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <CurrencyAmount value={unitPrice} decimals={2} align="flex-end" />
                      </TableCell>
                      <TableCell align="right">
                        {unitDisc > 0 ? (
                          <CurrencyAmount value={unitDisc} prefix="-" color="success.main" variant="caption" fontWeight={600} decimals={2} align="flex-end" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {itemDisc > 0 ? (
                          <CurrencyAmount value={itemDisc} prefix="-" color="success.main" variant="caption" fontWeight={600} decimals={2} align="flex-end" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        <CurrencyAmount value={lineTotal} variant="body2" fontWeight={700} decimals={2} align="flex-end" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Financial Ledger Breakdown */}
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: brand.canvas }}>
          <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Items Subtotal</Typography>
              <CurrencyAmount value={subTotal} variant="body2" decimals={2} />
            </Stack>

            {itemDiscounts > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="success.main">Item Offers Discount</Typography>
                <CurrencyAmount value={itemDiscounts} prefix="-" color="success.main" variant="body2" fontWeight={600} decimals={2} />
              </Stack>
            )}

            {couponDiscount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="success.main">Coupon Voucher Discount</Typography>
                <CurrencyAmount value={couponDiscount} prefix="-" color="success.main" variant="body2" fontWeight={600} decimals={2} />
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Delivery Charge</Typography>
              <CurrencyAmount value={deliveryCharge} variant="body2" decimals={2} />
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Tax (VAT 5%)</Typography>
              <CurrencyAmount value={taxTotal} variant="body2" decimals={2} />
            </Stack>

            <Divider sx={{ my: 0.5 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>Final Grand Total</Typography>
              <CurrencyAmount value={grandTotal} variant="subtitle1" fontWeight={700} color="primary.main" decimals={2} />
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
              <CurrencyAmount value={paidAmount} variant="body2" fontWeight={600} color="#2e7d32" decimals={2} />
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={700}>Balance Due</Typography>
              <CurrencyAmount
                value={balanceDue}
                variant="body2"
                fontWeight={700}
                color={balanceDue > 0 ? destructive.main : 'text.primary'}
                decimals={2}
              />
            </Stack>

            {order.note && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                <strong>Customer note:</strong> {order.note}
              </Typography>
            )}
          </Stack>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>

        {onViewDetails && (
          <Button
            variant="contained"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => onViewDetails(order)}
            sx={{
              bgcolor: brand.primary,
              '&:hover': { bgcolor: brand.primaryDark }
            }}
          >
            Full Order Details
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

OrderItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func
};

function CustomerDetailsDialog({ open, order, onClose }) {
  const customer = getOrderCustomer(order);
  const displayName = customer?.name?.trim() || 'Guest';
  const initial = (displayName !== 'Guest' ? displayName : customer?.mobile_number || '?')
    .charAt(0)
    .toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Customer details
        </Typography>
        {order ? (
          <Typography variant="caption" color="text.secondary">
            Order {getOrderNo(order)}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {!customer ? (
          <Alert severity="info">No customer information on this order.</Alert>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '1.25rem'
                }}
              >
                {initial}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {displayName}
                </Typography>
                {customer.mobile_number ? (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {customer.mobile_number}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
            <Divider sx={{ mb: 0.5 }} />
            <CustomerDetailRow
              label="Full name"
              value={
                <Typography variant="body2" fontWeight={600}>
                  {customer.name?.trim() || '—'}
                </Typography>
              }
            />
            <CustomerDetailRow
              label="Mobile"
              value={
                <Typography variant="body2" fontWeight={500}>
                  {customer.mobile_number || '—'}
                </Typography>
              }
            />
            <CustomerDetailRow
              label="Email"
              value={
                <Typography variant="body2" fontWeight={500} color={customer.email ? 'text.primary' : 'text.secondary'}>
                  {customer.email || '—'}
                </Typography>
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#1f5b40', '&:hover': { bgcolor: '#133a28' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CustomerDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default function OrderList() {
  const { showApiError } = useToast();
  const { currency } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Default to 180 days ago to encompass active historical orders
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(180, 'day'));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf('day'));

  const [detailsDialog, setDetailsDialog] = useState({ open: false, order: null });
  const [itemsDialog, setItemsDialog] = useState({ open: false, order: null });
  const [customerDialog, setCustomerDialog] = useState({ open: false, order: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, order: null });

  const nextPageRef = useRef(1);
  const scrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreRef = useRef(() => {});

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const buildListParams = useCallback(
    (pageNum) => {
      const params = {
        page: pageNum,
        limit: PAGE_LIMIT,
        // Crucial: Node backend buildDateFilter returns {} when empty, causing where.timestamp = NOW().
        // Passing safe fallback bounds guarantees reliable retrieval across the full dataset.
        from: dateFrom ? dayjs(dateFrom).format('YYYY-MM-DD') : '2020-01-01',
        to: dateTo ? dayjs(dateTo).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD')
      };
      if (searchDebounced) params.search = searchDebounced;
      if (statusFilter !== 'all') {
        params.status = statusFilter;
        params.order_status = statusFilter;
      }
      return params;
    },
    [searchDebounced, statusFilter, dateFrom, dateTo]
  );

  const fetchOrdersPage = useCallback(
    async (pageNum) => {
      const res = await api.get('/mobile/order/list', { params: buildListParams(pageNum) });
      if (res.data?.status === false) {
        throw new Error(res.data?.message || 'Failed to load orders');
      }
      return parseOrderListResponse(res);
    },
    [buildListParams]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setHasMore(true);
    nextPageRef.current = 1;
    try {
      const result = await fetchOrdersPage(1);
      setOrders(result.orders);
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = 2;
    } catch (err) {
      setOrders([]);
      setTotal(0);
      setHasMore(false);
      const status = err.response?.status;
      if (status === 401) {
        showApiError(
          err,
          err.response?.data?.message ||
            'Unable to load orders. Your session may not have access to this endpoint.'
        );
      } else {
        showApiError(err, 'Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchOrdersPage, showApiError]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || loadMoreInFlightRef.current) return;

    const pageNum = nextPageRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchOrdersPage(pageNum);
      setOrders((prev) => {
        const seen = new Set(prev.map((o) => o.id));
        const added = result.orders.filter((o) => !seen.has(o.id));
        return [...prev, ...added];
      });
      setTotal(result.total);
      const more = result.currentPage < result.totalPages;
      setHasMore(more);
      nextPageRef.current = pageNum + 1;
    } catch (err) {
      showApiError(err, 'Failed to load more orders');
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, fetchOrdersPage, showApiError]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root, rootMargin: '40px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDateFrom(dayjs().subtract(180, 'day'));
    setDateTo(dayjs().endOf('day'));
  };

  const handleDateFromChange = (value) => {
    setDateFrom(value);
    if (value && dateTo && dayjs(value).isAfter(dayjs(dateTo), 'day')) {
      setDateTo(value);
    }
  };

  const handleDateToChange = (value) => {
    setDateTo(value);
  };

  const colSpan = 10;

  return (
    <Box>
      <PageHeader
        title="Orders"
        subtitle="View and manage complete customer orders, items breakdown, and payment details."
        count={loading ? '…' : total}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={loadInitial} size="small" disabled={loading}>
              <ReloadOutlined />
            </IconButton>
          </Tooltip>
        }
      />

      <MainCard sx={{ mb: 2, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} content={false}>
        <Box sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
              Filters
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{ color: 'text.secondary', textTransform: 'none', minWidth: 0 }}
            >
              Clear all
            </Button>
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'flex-end' }}
              useFlexGap
              flexWrap="wrap"
            >
              <TextField
                size="small"
                placeholder="Search order #, token, or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
                sx={{
                  minWidth: { xs: '100%', md: 240 },
                  flex: { md: 1 },
                  maxWidth: { md: 420 },
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
              <DatePicker
                label="From date"
                value={dateFrom}
                onChange={handleDateFromChange}
                format="DD MMM YYYY"
                maxDate={dateTo || undefined}
                slotProps={datePickerSlotProps}
              />
              <DatePicker
                label="To date"
                value={dateTo}
                onChange={handleDateToChange}
                format="DD MMM YYYY"
                minDate={dateFrom || undefined}
                maxDate={dayjs().add(1, 'day')}
                slotProps={datePickerSlotProps}
              />
              <TextField
                select
                size="small"
                label="Order status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={filterFieldSx}
              >
                <MenuItem value="all">All</MenuItem>
                {ORDER_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </LocalizationProvider>
        </Box>
      </MainCard>

      <MainCard contentSX={{ p: 0 }} sx={{ borderRadius: 3 }}>
        {loading && orders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {!loading && orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {search || statusFilter !== 'all'
                ? 'No orders match your filters'
                : 'No orders found'}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={loadInitial}
            >
              Try again
            </Typography>
          </Box>
        ) : null}

        {!loading && orders.length > 0 ? (
          <TableContainer
            ref={scrollRef}
            component={Paper}
            elevation={0}
            sx={tableScrollSx}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1040 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Items</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Financials</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ordered</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order, idx) => {
                  const statusKey = resolveOrderStatus(order);
                  const paymentStatus = getPaymentStatus(order);
                  const isDelivery = String(order.mode).toLowerCase() === 'delivery';
                  const customer = getOrderCustomer(order);
                  const hasPromotion = Boolean(order.offer_id || Number(order.coupon_discount) > 0);
                  const grandTotalVal = order.grand_total != null ? Number(order.grand_total) : Number(order.total || 0);
                  const paidVal = Number(order.paid || 0);
                  const balanceDueVal = Number(order.balance_due != null ? order.balance_due : Math.max(0, grandTotalVal - paidVal));

                  return (
                    <ClickableTableRow
                      key={order.id ?? idx}
                      onClick={() => setDetailsDialog({ open: true, order })}
                    >
                      <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>

                      {/* Order info with promo badge */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {getOrderNo(order)}
                        </Typography>
                        {order.token_no != null && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Token #{order.token_no}
                          </Typography>
                        )}
                        {hasPromotion && (
                          <Chip
                            icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: '13px !important' }} />}
                            label={
                              order.coupon_discount > 0 ? (
                                <Stack direction="row" alignItems="center" spacing={0.35} component="span">
                                  <span>Promo -{order.coupon_discount}</span>
                                  <DirhamSymbol size={10} />
                                </Stack>
                              ) : (
                                'Offer Applied'
                              )
                            }
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              height: 19,
                              fontSize: '0.625rem',
                              fontWeight: 700,
                              mt: 0.5,
                              px: 0.25
                            }}
                          />
                        )}
                      </TableCell>

                      {/* Customer info */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {customer?.name?.trim() || 'Guest'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {customer?.mobile_number || '—'}
                        </Typography>
                      </TableCell>

                      {/* Mode with visual icon */}
                      <TableCell>
                        <Chip
                          icon={
                            isDelivery ? (
                              <LocalShippingOutlinedIcon sx={{ fontSize: '14px !important', color: '#1f5b40 !important' }} />
                            ) : (
                              <FastfoodOutlinedIcon sx={{ fontSize: '14px !important', color: '#ed6c02 !important' }} />
                            )
                          }
                          label={isDelivery ? 'Delivery' : 'Takeaway'}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            borderColor: isDelivery ? 'rgba(31, 91, 64, 0.25)' : 'rgba(237, 108, 2, 0.25)',
                            color: isDelivery ? '#1f5b40' : '#ed6c02'
                          }}
                        />
                      </TableCell>

                      {/* Items count */}
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600}>
                          {getItemCount(order)}
                        </Typography>
                      </TableCell>

                      {/* Financials: Grand total, Paid, Due */}
                      <TableCell align="right">
                        <CurrencyAmount
                          value={grandTotalVal}
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                          align="flex-end"
                          decimals={2}
                        />
                        {paidVal > 0 ? (
                          <Stack direction="row" spacing={0.35} justifyContent="flex-end" alignItems="center">
                            <Typography variant="caption" color="#2e7d32" fontWeight={600}>
                              Paid
                            </Typography>
                            <CurrencyAmount
                              value={paidVal}
                              variant="caption"
                              color="#2e7d32"
                              fontWeight={600}
                              decimals={2}
                            />
                          </Stack>
                        ) : null}
                        {balanceDueVal > 0 ? (
                          <Stack direction="row" spacing={0.35} justifyContent="flex-end" alignItems="center">
                            <Typography variant="caption" color="error.main" fontWeight={600}>
                              Due
                            </Typography>
                            <CurrencyAmount
                              value={balanceDueVal}
                              variant="caption"
                              color="error.main"
                              fontWeight={600}
                              decimals={2}
                            />
                          </Stack>
                        ) : null}
                      </TableCell>

                      {/* Payment status */}
                      <TableCell>
                        {paymentStatus === '—' ? (
                          '—'
                        ) : (
                          <Chip
                            label={paymentStatus}
                            size="small"
                            sx={paymentStatusChipSx(paymentStatus)}
                          />
                        )}
                      </TableCell>

                      {/* Order status */}
                      <TableCell>
                        {!statusKey ? (
                          '—'
                        ) : (
                          <Chip
                            label={getOrderStatusLabel(order)}
                            size="small"
                            sx={orderStatusChipSx(statusKey)}
                          />
                        )}
                      </TableCell>

                      {/* Ordered timestamp */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">
                          {formatDate(order.timestamp || order.createdAt)}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableActionCell>
                        <TableActions>
                          <TableActionButton
                            action="view"
                            title="View Full Order Details"
                            onClick={() => setDetailsDialog({ open: true, order })}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </TableActionButton>
                          <TableActionButton
                            action="view"
                            title="Order Items"
                            onClick={() => setItemsDialog({ open: true, order })}
                          >
                            <ShoppingBagOutlinedIcon fontSize="small" />
                          </TableActionButton>
                          {/* Customer Details action - commented out for now; kept for future reuse
                          <TableActionButton
                            action="edit"
                            title="Customer Details"
                            onClick={() => setCustomerDialog({ open: true, order })}
                          >
                            <PersonOutlineOutlinedIcon fontSize="small" />
                          </TableActionButton>
                          */}
                          <TableActionButton
                            action="view"
                            title="Payment & Refund Controls"
                            onClick={() => setPaymentDialog({ open: true, order })}
                          >
                            <CreditCardOutlinedIcon fontSize="small" />
                          </TableActionButton>
                        </TableActions>
                      </TableActionCell>
                    </ClickableTableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    ref={loadMoreSentinelRef}
                    colSpan={colSpan}
                    align="center"
                    sx={{ py: 1.5, border: 0 }}
                  >
                    {loadingMore ? (
                      <CircularProgress size={24} />
                    ) : !hasMore ? (
                      <Typography variant="caption" color="text.secondary">
                        All {total} {pluralize(total, 'order')} loaded
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Scroll for more
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </MainCard>

      {/* 9-Section Talabat-Style Comprehensive Details Dialog */}
      <OrderDetailsDialog
        open={detailsDialog.open}
        order={detailsDialog.order}
        onClose={() => setDetailsDialog({ open: false, order: null })}
        onOpenPaymentRefund={(order) => setPaymentDialog({ open: true, order })}
      />

      {/* Enhanced Order Items & Customizations Dialog */}
      <OrderItemsDialog
        open={itemsDialog.open}
        order={itemsDialog.order}
        onClose={() => setItemsDialog({ open: false, order: null })}
        onViewDetails={(order) => {
          setItemsDialog({ open: false, order: null });
          setDetailsDialog({ open: true, order });
        }}
      />

      {/* Customer Information Dialog */}
      <CustomerDetailsDialog
        open={customerDialog.open}
        order={customerDialog.order}
        onClose={() => setCustomerDialog({ open: false, order: null })}
      />

      {/* Payment & Refund Operations Dialog */}
      <OrderPaymentDialog
        open={paymentDialog.open}
        order={paymentDialog.order}
        onClose={() => setPaymentDialog({ open: false, order: null })}
        onRefreshOrder={() => fetchOrdersPage(1)}
      />
    </Box>
  );
}
